import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCreateGlow, useGlows } from '../hooks/useGlows'
import { SubscriptionGate } from '../components/SubscriptionGate'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { CategoryCombobox } from '../components/ui/CategoryCombobox'
import { ErrorBanner } from '../components/ErrorBanner'
import { ApiError } from '../api/client'
import { MAX_CATEGORY_LENGTH, mergeCategorySuggestions } from '../lib/categories'
import { REGIONS, type AuthMode, type Region } from '../api/types'

export function GlowCreate() {
  const create = useCreateGlow()
  const glows = useGlows()
  const navigate = useNavigate()

  // Suggestions = defaults + the owner's in-use categories (custom typed value
  // still allowed). `glows.data` is undefined while loading / on error, so the
  // merge degrades to DEFAULT_CATEGORIES only — create is never blocked on the
  // glows query.
  const categorySuggestions = useMemo(() => {
    const existing = (glows.data ?? [])
      .map((g) => g.category)
      .filter((c): c is string => !!c && c.trim() !== '')
    return mergeCategorySuggestions(existing)
  }, [glows.data])

  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [region, setRegion] = useState<Region>('us-central1')
  const [authMode, setAuthMode] = useState<AuthMode>('Private')
  const [hideBranding, setHideBranding] = useState(false)

  const [fieldErrors, setFieldErrors] = useState<{ name?: string }>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  // When the create 402s at the glow limit we want a clickable Upgrade
  // link in the banner (the generic string error can't carry one), so we
  // track the limit case as a flag alongside the message.
  const [limitReached, setLimitReached] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setFieldErrors({})
    setGeneralError(null)
    setLimitReached(false)
    try {
      const body: Parameters<typeof create.mutateAsync>[0] = {
        region,
        auth_mode: authMode,
      }
      if (hideBranding) body.hide_branding = true
      if (name.trim()) body.name = name.trim()
      if (title.trim()) body.title = title.trim()
      if (category.trim()) body.category = category.trim()
      const glow = await create.mutateAsync(body)
      navigate(`/glows/${glow.id}`)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'name_taken') {
          setFieldErrors({ name: 'That name is already in use.' })
          return
        }
        if (e.code === 'invalid_name' || e.code === 'name_reserved') {
          setFieldErrors({ name: e.message })
          return
        }
        if (e.code === 'glow_limit_reached') {
          setLimitReached(true)
          setGeneralError(
            "You've reached your glow limit. Upgrade your plan to create more.",
          )
          return
        }
      }
      setGeneralError(e instanceof Error ? e.message : 'Could not create glow.')
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-semibold">New glow</h1>
      <p className="mb-6 text-sm text-cream/70">
        Pick a region and the rest. You&apos;ll get an API key on the next page
        for deploying your static assets.
      </p>

      <Card>
        {generalError && (
          <ErrorBanner className="mb-4">
            {generalError}
            {limitReached && (
              <>
                {' '}
                <Link
                  to="/billing"
                  className="font-medium text-glow-600 hover:underline"
                >
                  Upgrade
                </Link>
              </>
            )}
          </ErrorBanner>
        )}

        <form className="space-y-5" onSubmit={submit}>
          <Field
            label="Title"
            hint="Optional. A free-text display name for this glow (up to 100 characters)."
          >
            <Input
              type="text"
              placeholder="My weekend project"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </Field>

          <Field
            label="Category"
            hint="Optional. Group this glow on your dashboard (up to 50 characters). Leave blank for Uncategorized."
          >
            <CategoryCombobox
              value={category}
              onChange={setCategory}
              suggestions={categorySuggestions}
              maxLength={MAX_CATEGORY_LENGTH}
              placeholder="Work"
            />
          </Field>

          <SubscriptionGate flag="can_choose_name">
            {({ locked }) => (
              <Field
                label="Beacon"
                hint={
                  locked
                    ? 'Leave blank — we&apos;ll generate one for you.'
                    : 'Optional. The subdomain label. Leave blank to autogenerate. Lowercase letters, digits, and hyphens; min 8 chars.'
                }
                error={fieldErrors.name}
              >
                <Input
                  type="text"
                  placeholder={locked ? 'adjective-noun (autogenerated)' : 'my-glow'}
                  value={locked ? '' : name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={locked}
                  maxLength={63}
                />
              </Field>
            )}
          </SubscriptionGate>

          <fieldset>
            <legend className="text-sm font-medium text-cream">Region</legend>
            <p className="text-xs text-muted">
              Pins your assets and per-user data to this region. Cannot be changed later.
            </p>
            <div className="mt-2 space-y-2">
              {REGIONS.map((r) => (
                <Radio
                  key={r.value}
                  label={r.label}
                  description={r.description}
                  value={r.value}
                  checked={region === r.value}
                  onChange={() => setRegion(r.value)}
                />
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-cream">Auth mode</legend>
            <div className="mt-2 space-y-2">
              <Radio
                label="Private"
                description="Only listed emails can sign in. Free."
                value="Private"
                checked={authMode === 'Private'}
                onChange={() => setAuthMode('Private')}
              />
              <SubscriptionGate flag="can_set_accounts">
                {({ locked }) => (
                  <Radio
                    label="Accounts"
                    description="Anyone can sign in (open self-serve accounts). Paid."
                    value="Accounts"
                    checked={authMode === 'Accounts'}
                    onChange={() => {
                      if (!locked) setAuthMode('Accounts')
                    }}
                    disabled={locked}
                  />
                )}
              </SubscriptionGate>
              <SubscriptionGate flag="can_set_public">
                {({ locked }) => (
                  <Radio
                    label="Public"
                    description="Anyone can visit. No signin required."
                    value="Public"
                    checked={authMode === 'Public'}
                    onChange={() => {
                      if (!locked) setAuthMode('Public')
                    }}
                    disabled={locked}
                  />
                )}
              </SubscriptionGate>
            </div>
          </fieldset>

          <SubscriptionGate flag="can_hide_branding">
            {({ locked }) => (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={hideBranding}
                  disabled={locked}
                  onChange={(e) => {
                    if (!locked) setHideBranding(e.target.checked)
                  }}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-cream">
                    Hide Backlit branding
                  </span>
                  <span className="block text-xs text-muted">
                    Removes the floating Backlit button from this glow.
                  </span>
                </span>
              </label>
            )}
          </SubscriptionGate>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create glow'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/glows')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string | undefined
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-cream">{label}</label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  )
}

function Radio({
  label,
  description,
  value,
  checked,
  onChange,
  disabled,
}: {
  label: string
  description: string
  value: string
  checked: boolean
  onChange: () => void
  disabled?: boolean
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${
        checked
          ? 'border-glow-500 bg-glow-50'
          : 'border-line hover:bg-surface-2'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-1"
      />
      <div>
        <p className="text-sm font-medium text-cream">{label}</p>
        <p className="text-xs text-muted">{description}</p>
      </div>
    </label>
  )
}
